/**
 * 用户提问组件 (AI Elements Migration)
 *
 * 使用原生 HTML + Tailwind CSS 构建问答界面
 * 支持功能：
 * - 单选和多选模式
 * - 自定义输入
 * - 多问题导航
 *
 * @module QuestionPrompt
 */

import React, { useState, useMemo, useCallback } from 'react';
import type { QuestionAskedEvent, QuestionOption } from '../../types/chat';

// ==================== 类型定义 ====================

export interface QuestionPromptProps {
  /** 提问事件数据 */
  question: QuestionAskedEvent;
  /** 提交答案回调 */
  onSubmit: (answers: string[][]) => void;
  /** 取消回调 */
  onDismiss: () => void;
}

// ==================== 子组件 ====================

/**
 * 选项按钮组件
 */
const OptionButton: React.FC<{
  option: QuestionOption;
  selected: boolean;
  multiple: boolean;
  onClick: () => void;
}> = ({ option, selected, multiple, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 rounded-lg border transition-all
        ${selected
          ? 'border-amber-400 bg-amber-50 text-amber-700'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }
        ${multiple ? 'pl-10' : ''}
      `}
    >
      <div className="flex items-center gap-3">
        {/* 选择指示器 */}
        <span className={`
          flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
          ${selected ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}
        `}>
          {selected && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
        
        {/* 选项内容 */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${selected ? 'text-amber-700' : 'text-gray-800'}`}>
            {option.label}
          </p>
          {option.description && (
            <p className={`text-xs mt-0.5 ${selected ? 'text-amber-600' : 'text-gray-500'}`}>
              {option.description}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

/**
 * 进度条组件
 */
const ProgressBar: React.FC<{ value: number; status: 'active' | 'success' }> = ({ value, status }) => {
  const bgColor = status === 'success' ? 'bg-green-500' : 'bg-amber-500';
  
  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-150 ${bgColor}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 用户提问组件
 *
 * 使用原生 HTML + Tailwind CSS 构建
 */
export const QuestionPrompt: React.FC<QuestionPromptProps> = ({
  question,
  onSubmit,
  onDismiss,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [customInputs, setCustomInputs] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = question.questions;
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex >= questions.length - 1;
  const isFirstQuestion = currentIndex === 0;

  // 当前问题的答案
  const currentAnswer = answers[currentIndex] || [];
  const currentCustomInput = customInputs[currentIndex] || '';

  // 计算进度百分比
  const progressPercent = useMemo(() => {
    const answeredCount = questions.reduce((count, _, index) => {
      const hasAnswer = (answers[index]?.length ?? 0) > 0 || 
                        (customInputs[index]?.trim()?.length ?? 0) > 0;
      return count + (hasAnswer ? 1 : 0);
    }, 0);
    return Math.round((answeredCount / questions.length) * 100);
  }, [questions, answers, customInputs]);

  // 处理选项选择
  const handleSelect = useCallback((label: string) => {
    if (currentQuestion.multiple) {
      // 多选模式
      setAnswers((prev) => {
        const existing = prev[currentIndex] || [];
        const isSelected = existing.includes(label);
        return {
          ...prev,
          [currentIndex]: isSelected
            ? existing.filter((l) => l !== label)
            : [...existing, label],
        };
      });
    } else {
      // 单选模式
      setAnswers((prev) => ({
        ...prev,
        [currentIndex]: [label],
      }));
    }
  }, [currentIndex, currentQuestion.multiple]);

  // 处理自定义输入
  const handleCustomInput = useCallback((value: string) => {
    setCustomInputs((prev) => ({
      ...prev,
      [currentIndex]: value,
    }));
  }, [currentIndex]);

  // 下一步/提交
  const handleNext = useCallback(async () => {
    if (isLastQuestion) {
      // 构建最终答案
      const finalAnswers = questions.map((q, index) => {
        const answer = answers[index] || [];
        const customInput = customInputs[index]?.trim();

        if (customInput) {
          if (q.multiple) {
            return [...answer, customInput];
          }
          return [customInput];
        }
        return answer;
      });

      setIsSubmitting(true);
      onSubmit(finalAnswers);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [isLastQuestion, questions, answers, customInputs, onSubmit]);

  // 上一步
  const handleBack = useCallback(() => {
    if (!isFirstQuestion) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [isFirstQuestion]);

  // 取消
  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  // 检查是否有答案（选项或自定义输入）
  const hasAnswer = currentAnswer.length > 0 || currentCustomInput.trim().length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* 头部 - 进度指示器 */}
      <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3 w-full">
          {/* 问题图标 */}
          <span className="p-1.5 rounded-md bg-amber-100 text-amber-600 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>

          {/* 标题和进度 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-900">
                问题 {currentIndex + 1} / {questions.length}
              </span>
              <span className="text-xs text-gray-500">
                {progressPercent}%
              </span>
            </div>
            {/* 进度条组件 */}
            <ProgressBar
              value={progressPercent}
              status={progressPercent === 100 ? 'success' : 'active'}
            />
          </div>

          {/* 多问题导航点 */}
          {questions.length > 1 && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {questions.map((_, index) => {
                const isActive = index === currentIndex;
                const isAnswered = (answers[index]?.length ?? 0) > 0 || 
                                   (customInputs[index]?.trim()?.length ?? 0) > 0;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`
                      w-2 h-2 rounded-full transition-all
                      ${isActive ? 'w-4 bg-amber-500' : isAnswered ? 'bg-amber-300' : 'bg-gray-300'}
                    `}
                    aria-label={`跳转到问题 ${index + 1}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* 问题内容 */}
      <div className="px-4 py-4">
        {/* 问题文本 */}
        <div className="mb-4">
          <p className="text-base font-medium text-gray-900">
            {currentQuestion.question}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {currentQuestion.multiple ? '可多选' : '选择一个答案'}，或输入自定义答案
          </p>
        </div>

        {/* 选项列表 */}
        <div className="space-y-2">
          {currentQuestion.options.map((option, index) => (
            <OptionButton
              key={`${option.label}-${index}`}
              option={option}
              selected={currentAnswer.includes(option.label)}
              multiple={currentQuestion.multiple || false}
              onClick={() => handleSelect(option.label)}
            />
          ))}
        </div>

        {/* 自定义输入 */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <label className="block text-xs text-gray-500 mb-1.5">
            或输入自定义答案：
          </label>
          <input
            type="text"
            value={currentCustomInput}
            onChange={(e) => handleCustomInput(e.target.value)}
            placeholder="输入你的答案..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm text-gray-700 placeholder-gray-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 focus:outline-none transition-all"
          />
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* 底部操作按钮 */}
      <div className="px-4 py-3 flex items-center justify-between">
        <button
          onClick={handleDismiss}
          disabled={isSubmitting}
          className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          取消
        </button>

        <div className="flex items-center gap-2">
          {!isFirstQuestion && (
            <button
              onClick={handleBack}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              上一步
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={isSubmitting || !hasAnswer}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
              isLastQuestion 
                ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                : 'border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {isSubmitting ? '提交中...' : isLastQuestion ? '提交' : '下一步'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionPrompt;
